import { useState, useEffect, useMemo, useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import MeasurementContext from "../../context/MeasurementContext";
import { getAlldevices } from "../../redux/apiCalls/devicesCall/devicesCall";
import "./DragDropDevices.css";
import { Funnel } from "lucide-react";

const norm = (v) =>
  String(v || "")
    .trim()
    .toLowerCase();

const normTag = (v) => norm(v).toUpperCase();

const deviceHasAnyVisibleTag = (device, requiredTagsUpper) => {
  if (!Array.isArray(requiredTagsUpper) || requiredTagsUpper.length === 0) {
    return true;
  }

  const basic = Array.isArray(device?.basic_parameters)
    ? device.basic_parameters
    : [];

  const calc = Array.isArray(device?.calculated_parameters)
    ? device.calculated_parameters
    : [];

  const basicOk = basic.some(
    (p) => p?.visible === 1 && requiredTagsUpper.includes(normTag(p?.tag))
  );

  const calcOk = calc.some(
    (p) =>
      p?.device_calculated_parameter?.visible === 1 &&
      requiredTagsUpper.includes(normTag(p?.tag))
  );

  return basicOk || calcOk;
};

const sameStringSet = (a, b) => {
  const aa = Array.isArray(a) ? a.filter(Boolean) : [];
  const bb = Array.isArray(b) ? b.filter(Boolean) : [];

  if (aa.length !== bb.length) return false;

  const sa = [...aa].sort();
  const sb = [...bb].sort();

  for (let i = 0; i < sa.length; i++) {
    if (sa[i] !== sb[i]) return false;
  }

  return true;
};

const sameOptionsByMac = (a, b) => {
  const aa = Array.isArray(a) ? a.map((x) => x?.mac).filter(Boolean) : [];
  const bb = Array.isArray(b) ? b.map((x) => x?.mac).filter(Boolean) : [];

  return sameStringSet(aa, bb);
};

const matchesSelectedLocation = (device, selected) => {
  const level = selected?.level;
  const selectedId = Number(selected?.id);
  const selectedCompanyId = Number(selected?.idCompany);

  if (!Number.isFinite(selectedId)) return false;

  if (level === "company") {
    return Number(device?.company_fk) === selectedId;
  }

  if (level === "branch_offices") {
    return Number(device?.branch_fk) === selectedId;
  }

  if (level === "branch_office_place") {
    return Number(device?.place_fk) === selectedId;
  }

  if (level === "place_zone") {
    return Number(device?.zone_fk) === selectedId;
  }

  if (level === "zone_local") {
    return Number(device?.local_fk) === selectedId;
  }

  // Fallback por compatibilidad con selecciones viejas o datos incompletos.
  return (
    Number(device?.company_fk) === selectedCompanyId ||
    Number(device?.company_fk) === selectedId ||
    Number(device?.branch_fk) === selectedId ||
    Number(device?.place_fk) === selectedId ||
    Number(device?.zone_fk) === selectedId ||
    Number(device?.local_fk) === selectedId
  );
};

const DragDropDevices = ({
  setDevicesSelected,
  firstSelect,
  second,
  primary,
  prefillDevices,
  maxSelections,
  requiredMeterTags,
  showParameters = true,
  lockMeter = false,
}) => {
  const dispatch = useDispatch();
  const { setMeterSelected, meterSelected } = useContext(MeasurementContext);
  const user = useSelector((state) => state.user.currentUser);
  const devices = useSelector((state) => state.devices.currentDevices);

  const [selectedMacs, setSelectedMacs] = useState([]);
  const [stableOptions, setStableOptions] = useState([]);

  const userInteractedRef = useRef(false);
  const lastSentMacsRef = useRef([]);

  useEffect(() => {
    getAlldevices(dispatch, user.accessToken);
  }, [dispatch, user.accessToken]);

  const requiredTagsUpper = useMemo(
    () =>
      Array.isArray(requiredMeterTags) ? requiredMeterTags.map(normTag) : [],
    [requiredMeterTags]
  );

  const selectedLocations = useMemo(() => {
    return Array.isArray(firstSelect)
      ? firstSelect
          .map((c) => ({
            id: Number(c?.id),
            idCompany: Number(c?.id_company),
            level: c?.level,
          }))
          .filter((c) => Number.isFinite(c.id))
      : [];
  }, [firstSelect]);

  const secondArr = useMemo(() => {
    if (Array.isArray(second)) return second.map(norm);
    if (second == null) return [];

    return [norm(second)];
  }, [second]);

  const deviceOptions = useMemo(() => {
    if (!Array.isArray(devices)) return [];
    if (!selectedLocations.length) return [];

    const primaryNorm = norm(primary);

    return devices
      .filter((d) => {
        const matchesLocation = selectedLocations.some((selected) =>
          matchesSelectedLocation(d, selected)
        );

        if (!matchesLocation) return false;

        if (primaryNorm) {
          if (norm(d?.primary_group) !== primaryNorm) return false;
        }

        if (secondArr.length) {
          const dSec = norm(d?.secondary_group);

          if (!secondArr.includes(dSec)) return false;
        }

        if (!deviceHasAnyVisibleTag(d, requiredTagsUpper)) return false;

        return true;
      })
      .map((device) => ({
        value: device.id,
        label: `${device.name} - ${device.place}`,
        mac: device.mac,
        data: device,
      }));
  }, [devices, selectedLocations, primary, secondArr, requiredTagsUpper]);

  useEffect(() => {
    if (!selectedLocations.length) {
      setStableOptions([]);
      setSelectedMacs([]);
      userInteractedRef.current = false;
      lastSentMacsRef.current = [];

      return;
    }

    if (!Array.isArray(deviceOptions) || deviceOptions.length === 0) return;

    setStableOptions((prev) => {
      if (sameOptionsByMac(prev, deviceOptions)) return prev;

      return deviceOptions;
    });
  }, [deviceOptions, selectedLocations]);

  const selectedDevices = useMemo(() => {
    if (!selectedMacs.length) return [];

    const set = new Set(selectedMacs);
    const opts = Array.isArray(stableOptions) ? stableOptions : [];

    return opts.filter((o) => set.has(o?.mac));
  }, [selectedMacs, stableOptions]);

  useEffect(() => {
    if (userInteractedRef.current) return;
    if (!Array.isArray(prefillDevices) || prefillDevices.length === 0) return;
    if (!Array.isArray(stableOptions) || stableOptions.length === 0) return;

    let macs = prefillDevices.map((d) => d?.mac).filter(Boolean);

    if (maxSelections && macs.length > maxSelections) {
      macs = macs.slice(0, maxSelections);
    }

    if (!sameStringSet(selectedMacs, macs)) {
      setSelectedMacs(macs);
    }
  }, [prefillDevices, stableOptions, maxSelections, selectedMacs]);

  useEffect(() => {
    if (sameStringSet(lastSentMacsRef.current, selectedMacs)) return;

    lastSentMacsRef.current = [...selectedMacs];
    setDevicesSelected(selectedDevices);
  }, [selectedMacs, selectedDevices, setDevicesSelected]);

  const getPriority = (tag) => {
    const t = norm(tag).toLowerCase();

    if (t.includes("potencia")) return 1;
    if (t.includes("energia")) return 2;
    if (t.includes("tension")) return 3;
    if (t.includes("corriente")) return 4;

    return 99;
  };

  const commonTags = useMemo(() => {
    if (!selectedDevices || selectedDevices.length === 0) return [];

    const extractVisibleParams = (device) => {
      const params = [];

      if (Array.isArray(device.data.basic_parameters)) {
        device.data.basic_parameters.forEach((param) => {
          if (param.tag && param.visible === 1) {
            params.push({
              tag: param.tag,
              type: "basic",
              id: param.idbasic_parameters,
              unit: param?.units || "",
            });
          }
        });
      }

      if (Array.isArray(device.data.calculated_parameters)) {
        device.data.calculated_parameters.forEach((param) => {
          if (param.tag && param?.device_calculated_parameter?.visible === 1) {
            params.push({
              tag: param.tag,
              type: "calculated",
              id: param.idcalculated_parameter,
              unit: param?.units || "",
            });
          }
        });
      }

      return params;
    };

    const allParamsPerDevice = selectedDevices.map(extractVisibleParams);

    return allParamsPerDevice
      .reduce((acc, curr) => {
        return acc.filter((paramA) =>
          curr.some((paramB) => norm(paramB.tag) === norm(paramA.tag))
        );
      })
      .sort((a, b) => getPriority(a.tag) - getPriority(b.tag));
  }, [selectedDevices]);

  useEffect(() => {
    const commonTagsNormalized = commonTags?.map((m) => norm(m?.tag));

    if (lockMeter && meterSelected?.tag && meterSelected?.type) {
      return;
    }

    if (
      commonTagsNormalized?.length > 0 &&
      (!meterSelected ||
        !commonTagsNormalized.includes(norm(meterSelected?.tag)))
    ) {
      let found;

      // Si hay tags requeridos, buscar primero uno de esos.
      if (Array.isArray(requiredMeterTags) && requiredMeterTags.length > 0) {
        const requiredNorm = requiredMeterTags.map(normTag);

        found = commonTags.find((ct) => {
          const tagNorm = normTag(ct.tag);

          return requiredNorm.some(
            (required) =>
              tagNorm === required ||
              tagNorm.includes(required) ||
              required.includes(tagNorm)
          );
        });
      }

      // Si no encontró uno requerido, usar el primero por prioridad.
      if (!found && commonTagsNormalized.length > 0) {
        found = commonTags.find(
          (ct) => norm(ct.tag) === commonTagsNormalized[0]
        );
      }

      if (found) {
        setMeterSelected({
          tag: norm(found.tag),
          type: found.type,
          id: found.id,
          unit: found?.unit,
        });
      }
    }
  }, [
    commonTags,
    meterSelected,
    setMeterSelected,
    requiredMeterTags,
    lockMeter,
  ]);

  const handleDeviceChange = (selected) => {
    userInteractedRef.current = true;

    const arr = Array.isArray(selected) ? selected : [];

    let macs = arr.map((o) => o?.mac).filter(Boolean);

    if (maxSelections && macs.length > maxSelections) {
      macs = macs.slice(-maxSelections);
    }

    setSelectedMacs(macs);
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#80bdff" : "#ced4da",
      boxShadow: state.isFocused
        ? "0 0 0 0.2rem rgba(0, 123, 255, 0.25)"
        : null,
      "&:hover": { borderColor: "#80bdff" },
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#007bff"
        : state.isFocused
          ? "#f8f9fa"
          : null,
      color: state.isSelected ? "white" : "#212529",
      "&:active": { backgroundColor: "#007bff" },
    }),
    multiValue: (base) => ({ ...base, backgroundColor: "#e9ecef" }),
    multiValueLabel: (base) => ({ ...base, color: "#495057" }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#6c757d",
      "&:hover": { backgroundColor: "#dc3545", color: "white" },
    }),
  };

  return (
    <div className="drag-devices-container">
      <div className="drag-devices-header">
        <Funnel size={16} className="drag-filter-icon" />
        <h3 className="drag-devices-title">Dispositivos</h3>
      </div>

      <Select
        isMulti
        name="devices"
        options={stableOptions}
        className="drag-devices-select"
        classNamePrefix="select"
        value={selectedDevices}
        onChange={handleDeviceChange}
        placeholder="Seleccionar dispositivos..."
        noOptionsMessage={() => "No hay dispositivos disponibles"}
        styles={customStyles}
        isClearable={true}
        getOptionValue={(opt) => String(opt?.mac)}
        getOptionLabel={(opt) => opt?.label}
        closeMenuOnSelect={false}
      />

      {commonTags?.length > 0 && showParameters && (
        <div className="parameters-buttons-section">
          <div className="buttons-container">
            {commonTags.map((meter, index) => (
              <button
                key={index}
                type="button"
                className={`parameter-button ${
                  norm(meterSelected?.tag) === norm(meter?.tag)
                    ? "selected"
                    : ""
                }`}
                onClick={() =>
                  setMeterSelected({
                    tag: norm(meter.tag),
                    type: meter.type,
                    id: meter.id,
                    unit: meter?.unit,
                  })
                }
              >
                {meter?.tag}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DragDropDevices;